import { Button, Form, Input, notification } from "antd";
import { ArrowLeft, ArrowRight, CheckCircle, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MicroticaLogoColor from "../../assets/logo-color.svg";
import { securityApi } from "../../backend/security";
import AuthVisualPanel from "../../components/auth/AuthVisualPanel";

export default function ActivationRequest() {
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    async function handleRequest(values: { email: string }) {
        try {
            setLoading(true);
            await securityApi.requestActivation(values.email);
            setEmailSent(true);
        } catch (error: any) {
            notification.error({
                message: "Unable to send activation email",
                description: error?.response?.data?.message || "Please wait a moment and try again."
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="login-container">
            <AuthVisualPanel />

            <div className="login-form-section">
                <div className="login-form-wrapper">
                    <div className="login-mobile-brand">
                        <Link to="/">
                            <img src={MicroticaLogoColor} alt="Microtica" />
                        </Link>
                    </div>

                    {emailSent ? (
                        <div className="signup-success">
                            <div className="signup-success-icon">
                                <CheckCircle size={48} />
                            </div>
                            <h2 className="signup-success-title">Check your email</h2>
                            <p className="signup-success-text">
                                If an unconfirmed account exists for that address, we'll send a new activation link.
                                Check your inbox and spam folder.
                            </p>
                            <Button type="default" size="large" className="signup-success-btn" onClick={() => setEmailSent(false)}>
                                Try a different email
                            </Button>
                            <div className="login-signup-link" style={{ marginTop: 24 }}>
                                <Link to="/auth/login">Back to sign in</Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="login-form-header">
                                <h2 className="login-form-title">Resend activation email</h2>
                                <p className="login-form-subtitle">
                                    Enter the email address you used to create your Microtica account.
                                </p>
                            </div>

                            <Form
                                form={form}
                                layout="vertical"
                                requiredMark="optional"
                                initialValues={{ email: searchParams.get("email") || "" }}
                                onFinish={handleRequest}
                                className="login-form"
                            >
                                <Form.Item
                                    name="email"
                                    required
                                    rules={[
                                        { required: true, message: "Please enter your email address" },
                                        { type: "email", message: "Please enter a valid email address" }
                                    ]}
                                >
                                    <Input
                                        placeholder="Email address"
                                        autoComplete="username"
                                        prefix={<Mail size={18} strokeWidth={1.5} />}
                                        className="login-input"
                                        size="large"
                                    />
                                </Form.Item>

                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    block
                                    size="large"
                                    loading={loading}
                                    className="login-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Send activation email</span>
                                            <ArrowRight size={18} className="login-submit-arrow" />
                                        </>
                                    )}
                                </Button>
                            </Form>

                            <div className="login-signup-link">
                                <Link to="/auth/login" className="back-to-login">
                                    <ArrowLeft size={16} />
                                    <span>Back to sign in</span>
                                </Link>
                            </div>
                        </>
                    )}
                </div>

                <div className="login-footer">
                    <span>© {new Date().getFullYear()} Microtica. All rights reserved.</span>
                </div>
            </div>
        </div>
    );
}
